import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { LandingLayout } from '@/components/landing/LandingLayout';
import { PainAwareLandingHero } from '@/components/landing/PainAwareLandingHero';
import { ValuePropsSection } from '@/components/landing/ValuePropsSection';
import { BottomCtaBlock } from '@/components/landing/BottomCtaBlock';
import { AiSmartAutomationSection } from '@/components/landing/AiSmartAutomationSection';
import { FaqSection } from '@/components/landing/FaqSection';
import { BeforeAfterScenario } from '@/components/landing/BeforeAfterScenario';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, Bell, DollarSign, Clock, Calendar, CheckCircle, XCircle, ArrowRight, Sparkles } from 'lucide-react';
import { AiAssistantSummary } from '@/components/landing/AiAssistantSummary';
import { StickyFloatingCTA } from '@/components/landing/StickyFloatingCTA';
import { TrustBadges } from '@/components/landing/TrustBadges';
import { StatsSection } from '@/components/landing/StatsSection';
import { TestimonialCarousel } from '@/components/landing/TestimonialCarousel';
import { RoiSection } from '@/components/landing/RoiSection';
import { ComparisonTable } from '@/components/landing/ComparisonTable';
import { FinalCTASection } from '@/components/landing/FinalCTASection';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const KeyPointsSection = () => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();
  const { t } = useTranslation();
  
  return (
    <section ref={ref} className="py-16 container mx-auto px-4 max-w-5xl">
      <h2 className="text-3xl font-bold mb-12 text-center">
        {t('cleanerTool.keyPoints.title')}
      </h2>
      <div className="grid md:grid-cols-3 gap-6">
        {[
          {
            icon: Users,
            title: t('cleanerTool.keyPoints.item1Title'),
            description: t('cleanerTool.keyPoints.item1Description'),
            badge: t('cleanerTool.keyPoints.item1Badge')
          },
          {
            icon: Bell,
            title: t('cleanerTool.keyPoints.item2Title'),
            description: t('cleanerTool.keyPoints.item2Description')
          },
          {
            icon: DollarSign,
            title: t('cleanerTool.keyPoints.item3Title'),
            description: t('cleanerTool.keyPoints.item3Description')
          }
        ].map((item, index) => (
          <Card 
            key={index}
            className={`group transition-all duration-700 hover:shadow-2xl hover:-translate-y-2 hover:scale-[1.02] ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
            style={{ transitionDelay: `${index * 150}ms` }}
          >
            <CardHeader>
              {item.badge && <Badge className="mb-2 w-fit animate-pulse hover:scale-110 transition-transform">{item.badge}</Badge>}
              <item.icon className="w-10 h-10 text-primary mb-2 transition-all duration-300 group-hover:rotate-12 group-hover:scale-125" />
              <CardTitle>{item.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {item.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};

const SeeHowItWorksSection = ({ navigate }: { navigate: any }) => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();
  const { t } = useTranslation();
  
  return (
    <section ref={ref} className="py-16 bg-gradient-to-b from-muted/30 to-background">
      <div className="container mx-auto px-4 max-w-6xl">
        <h2 className="text-3xl font-bold mb-4 text-center">{t('cleanerTool.seeHowItWorks.title')}</h2>
        <p className="text-center text-muted-foreground mb-12">{t('cleanerTool.seeHowItWorks.subtitle')}</p>
        
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              title: t('cleanerTool.seeHowItWorks.item1Title'),
              description: t('cleanerTool.seeHowItWorks.item1Description'),
              image: "/screenshots/portfolio.webp",
              alt: "Mobile cleaner dashboard showing assigned turnovers"
            },
            {
              title: t('cleanerTool.seeHowItWorks.item2Title'),
              description: t('cleanerTool.seeHowItWorks.item2Description'),
              image: "/screenshots/income.webp",
              alt: "Email reminder notification example"
            },
            {
              title: t('cleanerTool.seeHowItWorks.item3Title'),
              description: t('cleanerTool.seeHowItWorks.item3Description'),
              image: "/screenshots/expenses.webp",
              alt: "Payout tracking table with completed jobs"
            }
          ].map((item, index) => (
            <Card 
              key={index}
              className={`group hover:shadow-2xl hover:-translate-y-3 transition-all duration-500 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <CardHeader>
                <div className="overflow-hidden rounded-lg mb-4">
                  <img 
                    src={item.image}
                    alt={item.alt}
                    className="w-full h-48 object-cover rounded-lg group-hover:scale-110 transition-transform duration-500 ease-out"
                  />
                </div>
                <CardTitle className="text-xl">{item.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
        
        <div className="text-center mt-12">
          <Button size="lg" onClick={() => navigate('/auth')} className="hover-scale group active:scale-95 transition-all duration-300">
            {t('cleanerTool.seeHowItWorks.ctaText')} <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

const BeforeAfterScenarioSection = () => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();
  const { t } = useTranslation();
  
  return (
    <section ref={ref} className={`py-16 bg-gradient-to-r from-muted/50 to-secondary/10 transition-all duration-1000 ${
      isVisible ? "opacity-100" : "opacity-0"
    }`}>
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold mb-8 text-center">
          {t('cleanerTool.beforeAfter.title')}
        </h2>
        <BeforeAfterScenario
          beforeText={t('cleanerTool.beforeAfter.before')}
          afterText={t('cleanerTool.beforeAfter.after')}
        />
      </div>
    </section>
  );
};

const RealWorldTimelineSection = () => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();
  const { t } = useTranslation();
  
  return (
    <section ref={ref} className="py-16 bg-background">
      <div className="container mx-auto px-4 max-w-5xl">
        <h2 className="text-3xl font-bold mb-12 text-center">{t('cleanerTool.timeline.title')}</h2>
        
        <div className="grid md:grid-cols-2 gap-8">
          {/* Manual WhatsApp */}
          <Card className="border-destructive/50">
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="w-6 h-6 text-destructive" />
                <CardTitle className="text-destructive">{t('cleanerTool.timeline.manualTitle')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { time: "23:40", text: t('cleanerTool.timeline.manualStep1') },
                  { time: "23:45", text: t('cleanerTool.timeline.manualStep2') },
                  { time: "09:00", text: t('cleanerTool.timeline.manualStep3') },
                  { time: "11:00", text: t('cleanerTool.timeline.manualStep4') },
                  { time: "11:30", text: t('cleanerTool.timeline.manualStep5') }
                ].map((step, index) => (
                  <div 
                    key={index}
                    className={`flex items-start gap-3 transition-all duration-700 ${
                      isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-20"
                    }`}
                    style={{ transitionDelay: `${index * 150}ms` }}
                  >
                    <Badge variant="destructive" className="shrink-0 hover:animate-pulse transition-all hover:scale-110">{step.time}</Badge>
                    <p className="text-sm text-muted-foreground">{step.text}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Rental Manager */}
          <Card className="border-primary">
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-6 h-6 text-primary" />
                <CardTitle className="text-primary">{t('cleanerTool.timeline.autoTitle')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { time: "10:00", text: t('cleanerTool.timeline.autoStep1') },
                  { time: "—", text: t('cleanerTool.timeline.autoStep2') },
                  { time: "09:45", text: t('cleanerTool.timeline.autoStep3') },
                  { time: "11:30", text: t('cleanerTool.timeline.autoStep4') },
                  { time: "12:00", text: t('cleanerTool.timeline.autoStep5') }
                ].map((step, index) => (
                  <div 
                    key={index}
                    className={`flex items-start gap-3 transition-all duration-700 ${
                      isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-20"
                    }`}
                    style={{ transitionDelay: `${index * 150}ms` }}
                  >
                    <Badge variant="default" className="shrink-0 hover:animate-pulse transition-all hover:scale-110">{step.time}</Badge>
                    <p className="text-sm">{step.text}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

const EnhancedAiSection = ({ navigate }: { navigate: any }) => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();
  const { t } = useTranslation();
  
  return (
    <section ref={ref} className={`py-16 bg-gradient-to-r from-primary/5 to-secondary/10 transition-all duration-1000 ${
      isVisible ? "opacity-100" : "opacity-0"
    }`}>
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-8">
          <Badge className="mb-4 bg-primary/20 text-primary border-primary animate-[pulse_2s_ease-in-out_infinite] hover:scale-110 transition-transform shadow-lg">
            <Sparkles className="w-3 h-3 mr-1" /> {t('cleanerTool.aiSection.badge')}
          </Badge>
          <h2 className="text-3xl font-bold">{t('cleanerTool.aiSection.title')}</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <img 
              src="/screenshots/properties.webp"
              alt="Mobile cleaner dashboard"
              className="rounded-lg shadow-2xl border border-border animate-[float_3s_ease-in-out_infinite]"
            />
          </div>

          <Card>
            <CardContent className="pt-6">
              <ul className="space-y-4">
                {[
                  t('cleanerTool.aiSection.feature1'),
                  t('cleanerTool.aiSection.feature2'),
                  t('cleanerTool.aiSection.feature3'),
                  t('cleanerTool.aiSection.feature4'),
                  t('cleanerTool.aiSection.feature5'),
                  t('cleanerTool.aiSection.feature6')
                ].map((feature, index) => (
                  <li 
                    key={index} 
                    className={`flex items-start gap-3 transition-all duration-500 ${
                      isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"
                    }`}
                    style={{ transitionDelay: `${index * 80}ms` }}
                  >
                    <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0 group-hover:scale-125 group-hover:rotate-12 transition-all duration-300" />
                    <p className="text-sm">{feature}</p>
                  </li>
                ))}
              </ul>

              <Button 
                variant="outline" 
                className="w-full mt-6 group hover:shadow-lg transition-all duration-300 active:scale-95" 
                onClick={() => navigate('/auth')}
              >
                {t('cleanerTool.aiSection.ctaText')} <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </CardContent>
          </Card>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-8">
          {t('cleanerTool.aiSection.targetAudience')}
        </p>
      </div>
    </section>
  );
};

const CommonMistakesSection = () => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();
  const { t } = useTranslation();
  
  return (
    <section ref={ref} className="py-16 bg-muted/30">
      <div className="container mx-auto px-4 max-w-6xl">
        <h2 className="text-3xl font-bold mb-4 text-center">{t('cleanerTool.mistakes.title')}</h2>
        <p className="text-center text-muted-foreground mb-12">{t('cleanerTool.mistakes.subtitle')}</p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              mistake: t('cleanerTool.mistakes.mistake1Title'),
              solution: t('cleanerTool.mistakes.mistake1Solution'),
              impact: t('cleanerTool.mistakes.mistake1Impact')
            },
            {
              mistake: t('cleanerTool.mistakes.mistake2Title'),
              solution: t('cleanerTool.mistakes.mistake2Solution'),
              impact: t('cleanerTool.mistakes.mistake2Impact')
            },
            {
              mistake: t('cleanerTool.mistakes.mistake3Title'),
              solution: t('cleanerTool.mistakes.mistake3Solution'),
              impact: t('cleanerTool.mistakes.mistake3Impact')
            },
            {
              mistake: t('cleanerTool.mistakes.mistake4Title'),
              solution: t('cleanerTool.mistakes.mistake4Solution'),
              impact: t('cleanerTool.mistakes.mistake4Impact')
            },
            {
              mistake: t('cleanerTool.mistakes.mistake5Title'),
              solution: t('cleanerTool.mistakes.mistake5Solution'),
              impact: t('cleanerTool.mistakes.mistake5Impact')
            },
            {
              mistake: t('cleanerTool.mistakes.mistake6Title'),
              solution: t('cleanerTool.mistakes.mistake6Solution'),
              impact: t('cleanerTool.mistakes.mistake6Impact')
            }
          ].map((item, index) => (
            <Card 
              key={index}
              className={`group transition-all duration-700 hover:shadow-2xl hover:-translate-y-2 hover:animate-[wiggle_0.5s_ease-in-out] ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <CardHeader>
                <Badge variant="destructive" className="w-fit mb-2 hover:scale-110 transition-transform">{t('cleanerTool.mistakes.badgeLabel')} {index + 1}</Badge>
                <CardTitle className="text-lg">{item.mistake}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs font-semibold text-primary mb-1">✓ Solution:</p>
                  <p className="text-sm">{item.solution}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Impact:</p>
                  <p className="text-sm text-muted-foreground italic">{item.impact}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

const CleanerSchedulingTool = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('cleanerTool.seo.title')}</title>
        <meta name="description" content={t('cleanerTool.seo.description')} />
        <link rel="canonical" href={`https://rental.shift-box.com/${i18n.language}/cleaner-scheduling-tool-for-airbnb-hosts`} />
        
        {/* hreflang tags for all languages */}
        <link rel="alternate" hrefLang="en" href="https://rental.shift-box.com/en/cleaner-scheduling-tool-for-airbnb-hosts" />
        <link rel="alternate" hrefLang="es" href="https://rental.shift-box.com/es/cleaner-scheduling-tool-for-airbnb-hosts" />
        <link rel="alternate" hrefLang="de" href="https://rental.shift-box.com/de/cleaner-scheduling-tool-for-airbnb-hosts" />
        <link rel="alternate" hrefLang="fr" href="https://rental.shift-box.com/fr/cleaner-scheduling-tool-for-airbnb-hosts" />
        <link rel="alternate" hrefLang="it" href="https://rental.shift-box.com/it/cleaner-scheduling-tool-for-airbnb-hosts" />
        <link rel="alternate" hrefLang="x-default" href="https://rental.shift-box.com/en/cleaner-scheduling-tool-for-airbnb-hosts" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "SoftwareApplication",
                "name": "Rental Manager - Cleaner Scheduling Tool",
                "applicationCategory": "BusinessApplication",
                "operatingSystem": "Web",
                "offers": {
                  "@type": "Offer",
                  "price": "12",
                  "priceCurrency": "EUR"
                },
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": "4.8",
                  "reviewCount": "124"
                }
              },
              {
                "@type": "HowTo",
                "name": "How to Schedule Cleaners for Airbnb",
                "step": [
                  {
                    "@type": "HowToStep",
                    "name": "Add your properties",
                    "text": "Set up your rental properties in the system"
                  },
                  {
                    "@type": "HowToStep",
                    "name": "Invite cleaners",
                    "text": "Send invites to your cleaning staff"
                  },
                  {
                    "@type": "HowToStep",
                    "name": "Assign jobs",
                    "text": "Assign turnovers to specific cleaners"
                  },
                  {
                    "@type": "HowToStep",
                    "name": "System sends reminders",
                    "text": "Automatic reminders are sent 24 hours before each job"
                  }
                ]
              },
              {
                "@type": "FAQPage",
                "mainEntity": ((t('cleanerTool.seo.faq', { returnObjects: true }) || []) as Array<{ question: string; answer: string }>).map((faq) => ({
                  "@type": "Question",
                  "name": faq.question,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                  }
                }))
              }
            ]
          })}
        </script>
      </Helmet>

      <LandingLayout>
        <StickyFloatingCTA />
        
        {/* Hero */}
        <div className="animate-fade-in">
          <PainAwareLandingHero
            h1={t('cleanerTool.hero.h1')}
            subtitle={t('cleanerTool.hero.subtitle')}
            ctaText={t('cleanerTool.hero.ctaText')}
            ctaAction={() => navigate('/auth')}
            visualContent={
              <div className="relative">
                <Badge className="absolute -top-4 left-1/2 -translate-x-1/2 z-10 animate-[fade-in_0.5s_ease-out_0.1s_both] hover:scale-110 transition-transform shadow-lg">
                  {t('cleanerTool.hero.badge')}
                </Badge>
                <div className="overflow-hidden rounded-lg">
                  <img 
                    src="/screenshots/properties.webp" 
                    alt="Mobile cleaner schedule view showing assigned turnovers"
                    className="rounded-lg shadow-2xl border border-border mt-4 hover:scale-110 transition-transform duration-500 ease-out animate-[slide-in-right_0.7s_ease-out_0.3s_both]"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg animate-[scale-in_0.5s_ease-out_0.5s_both]">
                  <p className="text-sm font-semibold">📱 847 cleaners using this daily</p>
                </div>
              </div>
            }
          />
        </div>

        {/* Trust Badges */}
        <TrustBadges />

        {/* Stats Section */}
        <StatsSection />

        {/* Unique focus paragraph */}
        <section className="py-8 container mx-auto px-4 max-w-2xl">
          <Badge className="mx-auto block w-fit mb-4">{t('cleanerTool.focus.badge')}</Badge>
          <Card className="border-primary/30">
            <CardContent className="pt-6">
              <p 
                className="text-lg text-center text-muted-foreground"
                dangerouslySetInnerHTML={{ __html: t('cleanerTool.focus.text') }}
              />
            </CardContent>
          </Card>
        </section>

        {/* Key points section */}
        <KeyPointsSection />

        {/* See How It Works */}
        <SeeHowItWorksSection navigate={navigate} />

        {/* Testimonials */}
        <section className="py-16 bg-gradient-to-b from-background to-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-4 text-center">{t('cleanerTool.testimonials.title')}</h2>
            <p className="text-center text-muted-foreground mb-12">{t('cleanerTool.testimonials.subtitle')}</p>
            <TestimonialCarousel testimonials={[
              {
                name: t('cleanerTool.testimonials.tom.name'),
                role: t('cleanerTool.testimonials.tom.role'),
                content: t('cleanerTool.testimonials.tom.content'),
                rating: 5
              },
              {
                name: t('cleanerTool.testimonials.maria.name'),
                role: t('cleanerTool.testimonials.maria.role'),
                content: t('cleanerTool.testimonials.maria.content'),
                rating: 5
              },
              {
                name: t('cleanerTool.testimonials.jessica.name'),
                role: t('cleanerTool.testimonials.jessica.role'),
                content: t('cleanerTool.testimonials.jessica.content'),
                rating: 5
              },
              {
                name: t('cleanerTool.testimonials.alex.name'),
                role: t('cleanerTool.testimonials.alex.role'),
                content: t('cleanerTool.testimonials.alex.content'),
                rating: 5
              },
              {
                name: t('cleanerTool.testimonials.sarah.name'),
                role: t('cleanerTool.testimonials.sarah.role'),
                content: t('cleanerTool.testimonials.sarah.content'),
                rating: 5
              },
              {
                name: t('cleanerTool.testimonials.david.name'),
                role: t('cleanerTool.testimonials.david.role'),
                content: t('cleanerTool.testimonials.david.content'),
                rating: 5
              }
            ]} />
          </div>
        </section>

        {/* ROI Section */}
        <RoiSection />

        {/* Comparison Table */}
        <ComparisonTable 
          title={t('cleanerTool.comparison.title')}
          rows={[
            { feature: t('cleanerTool.comparison.rows.cost'), manual: true, spreadsheet: true, rentalManager: true },
            { feature: t('cleanerTool.comparison.rows.perCleanerView'), manual: false, spreadsheet: false, rentalManager: true },
            { feature: t('cleanerTool.comparison.rows.autoReminders'), manual: false, spreadsheet: false, rentalManager: true },
            { feature: t('cleanerTool.comparison.rows.payoutTracking'), manual: false, spreadsheet: false, rentalManager: true },
            { feature: t('cleanerTool.comparison.rows.mobileFriendly'), manual: true, spreadsheet: false, rentalManager: true },
            { feature: t('cleanerTool.comparison.rows.lockCodes'), manual: false, spreadsheet: false, rentalManager: true },
            { feature: t('cleanerTool.comparison.rows.privacy'), manual: false, spreadsheet: false, rentalManager: true },
            { feature: t('cleanerTool.comparison.rows.photoProof'), manual: false, spreadsheet: false, rentalManager: false }
          ]}
        />

        {/* Before/After Scenario */}
        <BeforeAfterScenarioSection />

        {/* Real-World Timeline */}
        <RealWorldTimelineSection />

        {/* AI/Smart Automation */}
        <EnhancedAiSection navigate={navigate} />

        {/* Common Mistakes */}
        <CommonMistakesSection />

        {/* Value Props */}
        <ValuePropsSection />

        {/* Final CTA */}
        <FinalCTASection />

        {/* FAQ */}
        <FaqSection faqs={(t('cleanerTool.seo.faq', { returnObjects: true }) || []) as Array<{ question: string; answer: string }>} />

        {/* Positioning */}
        <section className="py-8 container mx-auto px-4 text-center">
          <blockquote className="text-lg italic text-muted-foreground max-w-3xl mx-auto border-l-4 border-primary pl-6">
            {t('cleanerTool.positioning.quote')}
            <footer className="text-sm mt-2 not-italic">{t('cleanerTool.positioning.footer')}</footer>
          </blockquote>
        </section>

        {/* AI Assistant Summary */}
        <AiAssistantSummary />

      {/* Bottom CTA */}
      <BottomCtaBlock />
      </LandingLayout>
    </>
  );
};

export default CleanerSchedulingTool;
